import '@fortawesome/fontawesome-pro/css/all.css';
import '@zyno-io/vue-foundation/dist/vue-foundation.css';
import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import { isEmbeddedApp } from '@/global.shared';
import { getRouteLocationIds, registerCurrentRouteIdsProvider } from '@/openapi-clients/helpers';
import { configurePublicApiClient } from '@/openapi-clients/public';
import { excludeChunkLoadErrors } from '@/shared/helpers/chunk-load-error';
import { excludeNoResponseFetchErrors } from '@/shared/helpers/no-response-fetch-error';
import { setupVf } from '@/vf.setup';

import router from './cx.routes';
import CxRoot from './cx.vue';
import { excludeFirefoxReaderModeErrors } from './sentry';
import './cx.state';

// Mark this runtime as the embedded CX app so shared components (e.g. the Loader) can adapt without
// every call site opting in. Set before mount so the first render already sees it.
isEmbeddedApp.value = true;

configurePublicApiClient();
registerCurrentRouteIdsProvider(() => getRouteLocationIds(router.currentRoute.value));

const app = createApp(CxRoot);

if (import.meta.env.VITE_APP_SENTRY_DSN) {
    const matches = import.meta.env.VITE_APP_SENTRY_DSN.match(/\/\/(.+)@.+\/(\d+)$/);
    if (matches) {
        Sentry.init({
            app: app,
            dsn: import.meta.env.VITE_APP_SENTRY_DSN,
            environment: import.meta.env.MODE,
            release: `zynosuite-spa@${import.meta.env.MODE}-${import.meta.env.VITE_APP_VERSION}`,
            tunnel: `https://sentry.s24.dev/proxy/${matches[2]}/${matches[1]}`,
            beforeSend: (event, hint) =>
                excludeFirefoxReaderModeErrors(event) && excludeChunkLoadErrors(event) && excludeNoResponseFetchErrors(event, hint),
            beforeBreadcrumb(breadcrumb) {
                const url = breadcrumb.category === 'fetch' ? breadcrumb.data?.url : null;
                return url?.includes('s24.dev') ? null : breadcrumb;
            }
        });
    }
}

setupVf(app);

app.use(router);

const pinia = createPinia();
app.use(pinia);

app.mount(document.body);
